import { Plugin } from 'chart.js';

const candidateTickIntervals: {
  unit: string;
  step: number;
  ms: number;
}[] = [
  //
  {unit: 'milliseconds', step: 1, ms: 1},
  {unit: 'milliseconds', step: 10, ms: 10},
  {unit: 'milliseconds', step: 50, ms: 50},
  {unit: 'milliseconds', step: 100, ms: 100},
  {unit: 'milliseconds', step: 250, ms: 250},
  {unit: 'milliseconds', step: 500, ms: 500},
  //
  {unit: 'seconds', step: 1, ms: 1000},
  {unit: 'seconds', step: 5, ms: 5000},
  {unit: 'seconds', step: 10, ms: 10000},
  {unit: 'seconds', step: 15, ms: 15000},
  {unit: 'seconds', step: 30, ms: 30000},
  //
  {unit: 'minutes', step: 1, ms: 60000},
  {unit: 'minutes', step: 5, ms: 300000},
  {unit: 'minutes', step: 10, ms: 600000},
  {unit: 'minutes', step: 15, ms: 900000},
  {unit: 'minutes', step: 30, ms: 1800000},
  //
  {unit: 'hours', step: 1, ms: 3600000},
  {unit: 'hours', step: 3, ms: 10800000},
  {unit: 'hours', step: 6, ms: 21600000},
  {unit: 'hours', step: 12, ms: 43200000},
  //
  {unit: 'days', step: 1, ms: 86400000},
  {unit: 'days', step: 2, ms: 172800000},
  {unit: 'days', step: 7, ms: 604800000},
  //
  {unit: 'months', step: 1, ms: 2628000000},
  {unit: 'months', step: 3, ms: 7884000000},
  {unit: 'years', step: 1, ms: 31536000000},
];

const conversionToMs: Record<string, number> = {
  milliseconds: 1,
  seconds: 1000,
  minutes: 60000,
  hours: 3600000,
  days: 86400000,
};

export const timeBasedTicks: Plugin = {

  id: 'timeBasedTicks',

  afterBuildTicks: (
    _,
    args,
    options: {
      debug?: boolean;
      disabled?: boolean;
      valueAxis?: 'x' | 'y';
      valueUnit?: string;
    },
  ) => {

    if (options.disabled) {
      return;
    }

    const valueUnit = options.valueUnit;

    if (
      !valueUnit ||
      (valueUnit !== 'milliseconds' &&
        valueUnit !== 'seconds' &&
        valueUnit !== 'minutes' &&
        valueUnit !== 'hours' &&
        valueUnit !== 'days')
    ) {
      return;
    }

    const scale = args.scale;

    if (scale.axis !== options.valueAxis) {
      return;
    }

    // rebuild the scale ticks to better represent time-based values

    const datasetRange = (scale as any)?._range;

    const datasetMin =
      datasetRange && typeof datasetRange.min !== 'undefined'
        ? datasetRange.min
        : scale.min;

    const datasetMax =
      datasetRange && typeof datasetRange.max !== 'undefined'
        ? datasetRange.max
        : scale.max;

    const findOptimalTickValue = () => {

      const factor = conversionToMs[valueUnit];

      const minInMs = datasetMin * factor;
      const maxInMs = datasetMax * factor;

      const rangeInMs = maxInMs - minInMs;

      const lowerTargetNumberOfTicks = 5;
      const upperTargetNumberOfTicks = 10;

      const targetTickCount = (lowerTargetNumberOfTicks + upperTargetNumberOfTicks) / 2;

      const allowedCandidates =
        candidateTickIntervals.filter(
          (candidate) => candidate.ms >= factor
        );

      let bestCandidateInRange = null;
      let bestCandidateInRangeDistanceFromTarget = Infinity;

      let bestCandidateAboveRange = null;
      let bestCandidateAboveRangeDistanceFromTarget = Infinity;

      let bestCandidateBelowRange = null;
      let bestCandidateBelowRangeDistanceFromTarget = Infinity;

      allowedCandidates.forEach((candidate) => {

        const tickCount = Math.ceil(rangeInMs / candidate.ms) + 1;

        if (tickCount >= lowerTargetNumberOfTicks
          && tickCount <= upperTargetNumberOfTicks) {

          const distance = Math.abs(tickCount - targetTickCount);

          if (distance < bestCandidateInRangeDistanceFromTarget) {

            bestCandidateInRange = candidate;
            bestCandidateInRangeDistanceFromTarget = distance;
          }

        } else if (tickCount > upperTargetNumberOfTicks) {

          const distance = tickCount - upperTargetNumberOfTicks;

          if (distance < bestCandidateAboveRangeDistanceFromTarget) {

            bestCandidateAboveRange = candidate;
            bestCandidateAboveRangeDistanceFromTarget = distance;
          }

        } else if (tickCount < lowerTargetNumberOfTicks) {

          const distance = lowerTargetNumberOfTicks - tickCount;

          if (distance < bestCandidateBelowRangeDistanceFromTarget) {

            bestCandidateBelowRange = candidate;
            bestCandidateBelowRangeDistanceFromTarget = distance;
          }
        }
      });

      let chosenCandidate: any = bestCandidateInRange;

      if (!chosenCandidate) {

        if (bestCandidateAboveRange
          && (!bestCandidateBelowRange || bestCandidateAboveRangeDistanceFromTarget <= bestCandidateBelowRangeDistanceFromTarget)) {

          chosenCandidate = bestCandidateAboveRange;

        } else if (bestCandidateBelowRange) {
          chosenCandidate = bestCandidateBelowRange;

        } else {
          chosenCandidate = allowedCandidates[0];
        }
      }

      return (chosenCandidate.ms / factor);
    };

    const optimalTickValue = findOptimalTickValue();

    const tickCount = Math.ceil((datasetMax - datasetMin) / optimalTickValue);

    const extendedMax = datasetMin + tickCount * optimalTickValue;

    const ticks: { label: string; value: number }[] = [];

    let tickValue = datasetMin;

    while (tickValue <= extendedMax) {

      ticks.push({
        label: (scale.options as any).ticks.callback.call(scale, tickValue, ticks.length, ticks),
        value: tickValue,
      });

      tickValue += optimalTickValue;

      // remove any floating-point imprecision
      tickValue = parseFloat(tickValue.toFixed(6));
    }

    scale.ticks = ticks;
  },

  afterLayout(
    chart,
    _,
    options: {
      disabled?: boolean;
    },
  ) {

    if (options.disabled) {
      return;
    }

    Object.values(chart.scales)
      .forEach((scale) => {

        const ticks = scale.ticks;

        if (ticks && ticks.length > 1) {

          const lastTick = ticks[ticks.length - 1];
          const prevTick = ticks[ticks.length - 2];

          if (lastTick.label === prevTick.label) {
            lastTick.label = '';
          }
        }
      });
  },
};
